import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'

import routes from './routes'
import { syncSessionCompany } from '../helpers/companySession'
import { companyHasLoyalty } from '../helpers/loyalty'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

// Barra de progreso fina arriba de todo mientras se baja el chunk de una
// pantalla (o algo que la pantalla necesita antes de mostrarse, ver
// `trackRouteLoad`). Sin ella la vista anterior queda congelada sin aviso.
// Solo aparece si la carga tarda más de 150 ms y nunca en la carga inicial
// (se habilita recién cuando la primera navegación terminó). Vive en <body>,
// fuera de #app, así que no depende del layout. Estilos en App.vue.
const routeProgress = (() => {
  let enabled = false
  let pending = 0
  let showTimer = null
  let hideTimer = null
  let bar = null

  function show() {
    showTimer = null
    clearTimeout(hideTimer)
    if (!bar) {
      bar = document.createElement('div')
      bar.className = 'route-progress'
      bar.setAttribute('aria-hidden', 'true')
      document.body.appendChild(bar)
    }
    bar.classList.remove('is-done')
    // Arranca la transición desde el ancho 0.
    void bar.offsetWidth
    bar.classList.add('is-loading')
  }

  function done() {
    clearTimeout(showTimer)
    showTimer = null
    if (!bar || !bar.classList.contains('is-loading')) return
    bar.classList.add('is-done')
    hideTimer = setTimeout(() => bar.classList.remove('is-loading', 'is-done'), 450)
  }

  return {
    enable() {
      enabled = true
    },
    track(promise) {
      if (!enabled || !promise || typeof promise.then !== 'function') return promise
      pending += 1
      if (pending === 1 && !showTimer) showTimer = setTimeout(show, 150)
      const settle = () => {
        pending = Math.max(0, pending - 1)
        if (pending === 0) done()
      }
      promise.then(settle, settle)
      return promise
    },
  }
})()

export const trackRouteLoad = promise => routeProgress.track(promise)

// Las vistas son `() => import(...)`: se envuelven para que la barra siga la
// descarga del chunk. vue-router guarda el componente resuelto, así que el
// wrapper corre una sola vez por vista.
function trackLazyComponents(list) {
  list.forEach(route => {
    const component = route.component
    if (typeof component === 'function' && !component.cid) {
      route.component = (...args) => routeProgress.track(component(...args))
    }
    if (route.children) trackLazyComponents(route.children)
  })
}
trackLazyComponents(routes)

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  
    const publicPages = ['/login', '/register', '/forgot-password', '/accept-invite', '/verify-device'];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = localStorage.getItem('auth.currentUser');
    
    if (authpage && !loggeduser) {
      return next('/login');
    }

    // Each tab works on its own company: a new tab starts on the last one chosen,
    // and a tab whose company is not part of the session any more loses it (and
    // its project) before any screen reads them.
    if (loggeduser) {
      syncSessionCompany();
    }

    if(routeTo.path=='/' &&loggeduser && JSON.parse(loggeduser).user.role =='host'){
      return next('/next-events');
    }

    if(loggeduser && routeTo.meta && routeTo.meta.roles && routeTo.meta.roles.indexOf(JSON.parse(loggeduser).user.role)==-1){
      return next('/404');
    }

    // Lealtad se contrata por cuenta: sin el módulo, cualquiera de sus pantallas
    // lleva a la que explica de qué se trata y cómo activarlo.
    if(loggeduser && routeTo.meta && routeTo.meta.product == 'loyalty' && !companyHasLoyalty()){
      return next({ name: 'loyalty-locked' });
    }

    next(); 
  
})

// Destino de la navegación en curso: si falla el chunk (deploy nuevo que borró
// los viejos), la recarga tiene que llevar a esa pantalla y no a la anterior,
// porque la URL nunca cambió.
let pendingTarget = null
router.beforeEach((routeTo, routeFrom, next) => {
  pendingTarget = routeTo.fullPath
  next()
})
router.afterEach(() => {
  pendingTarget = null
})

const chunkErrorPattern = /ChunkLoadError|Loading (?:CSS )?chunk [\s\S]*failed|CSS_CHUNK_LOAD_FAILED/i
const chunkReloadKey = 'router:chunk-reload'

router.onError(error => {
  const target = pendingTarget ? router.resolve(pendingTarget).href : undefined
  pendingTarget = null
  // app-update.js activa la versión nueva y recarga una sola vez por build.
  if (window.appUpdate) {
    window.appUpdate.recoverChunkError(error, target)
    return
  }
  // Sin app-update (p. ej. `serve`): una recarga completa al destino, salvo que
  // ya hayamos recargado hace menos de un minuto (evita un loop).
  const message = error && (error.message || error.name || String(error))
  if (!chunkErrorPattern.test(message || '')) return
  try {
    const last = Number(sessionStorage.getItem(chunkReloadKey)) || 0
    if (Date.now() - last < 60000) return
    sessionStorage.setItem(chunkReloadKey, String(Date.now()))
  } catch (storageError) {
    return
  }
  window.location.assign(target || window.location.href)
})

router.onReady(routeProgress.enable, routeProgress.enable)

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

export default router
